import { NewColumnMapping, NewSheetColumnMappings, ColumnMappingModel } from "services/DataImportService";
import { canonicalWorksheetName, SheetMapping } from "../../sheetMappings";
import {
  buildColumnKey,
  ColumnSource,
  findColumnSource,
  parseColumnKey,
  SHEET_COLUMN_SEPARATOR,
} from "../../sheetSources";
import { FieldResolution, ResolvedMapping, ResolvedMappingState, ResolvedWorksheetMapping } from "./mappingResolution";
import {
  areRowIdentifierFieldsEqual,
  buildRowIdentifierFieldsByWorksheet,
  getRowIdentifierOrder,
} from "./rowIdentifierFields";

export type MappingWithRowIdentifiers = {
  mapping: ResolvedMapping;
  rowIdentifierFields: Record<string, string[]>;
};

export type HydratedMapping = { resolved: ResolvedMappingState; rowIdentifierFields: Record<string, string[]> };

// Persisted rows don't record how a column was matched, so resolutions rehydrate as manual or notSupplied.
// When two sheets map the same field, the main sheet's column wins.
export function resolveMappingRows(
  rows: ColumnMappingModel[],
  sheetMappings: SheetMapping[],
  // Template spellings for another submission's worksheet names; worksheets outside them are dropped
  canonicalWorksheets?: string[]
): HydratedMapping | null {
  if (rows.length === 0) return null;
  const sheetMappingsByID = new Map(
    sheetMappings.map((sheetMapping) => [sheetMapping.submissionSheetMappingID, sheetMapping])
  );
  const resolveSheetMappingWorksheet = (sheetMapping: SheetMapping) =>
    canonicalWorksheets ? canonicalWorksheetName(sheetMapping.worksheet, canonicalWorksheets) : sheetMapping.worksheet;
  const sheetMappingsByWorksheet = groupSheetMappingsByWorksheet(sheetMappings, resolveSheetMappingWorksheet);

  const sorted = [...rows].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const mappingRowByField = new Map<
    string,
    { worksheet: string; row: ColumnMappingModel; sheetMapping: SheetMapping }
  >();
  for (const row of sorted) {
    const sheetMapping = row.submissionSheetMappingID ? sheetMappingsByID.get(row.submissionSheetMappingID) : undefined;
    const worksheet = sheetMapping ? resolveSheetMappingWorksheet(sheetMapping) : null;
    if (!sheetMapping || !worksheet || !row.targetColumnName) continue;

    const fieldKey = JSON.stringify([worksheet, row.targetColumnName]);
    const mappedFrom = mappingRowByField.get(fieldKey);
    if (mappedFrom && mappedFrom.sheetMapping.sourceOrder < sheetMapping.sourceOrder) continue;
    mappingRowByField.set(fieldKey, { worksheet, row, sheetMapping });
  }

  const resolved: ResolvedMappingState = { mapping: {}, resolutions: {} };
  const worksheetRows = [...mappingRowByField.values()];
  for (const { worksheet, row, sheetMapping } of worksheetRows) {
    const field = row.targetColumnName as string;
    resolved.mapping[worksheet] = resolved.mapping[worksheet] ?? {};
    resolved.resolutions[worksheet] = resolved.resolutions[worksheet] ?? {};
    const resolution: FieldResolution = row.sourceColumnName ? "manual" : "notSupplied";
    resolved.mapping[worksheet][field] = row.sourceColumnName
      ? buildSavedColumnKey(row.sourceColumnName, sheetMapping, sheetMappingsByWorksheet.get(worksheet) ?? [])
      : null;
    resolved.resolutions[worksheet][field] = resolution;
  }

  if (Object.keys(resolved.mapping).length === 0) return null;
  return { resolved, rowIdentifierFields: buildRowIdentifierFieldsByWorksheet(worksheetRows) };
}

// Splits the worksheet's mapping across its sheet mappings, saving each column's header as it is on its sheet.
// "Not supplied" goes on the main sheet's sheet mapping; a column whose sheet is no longer linked isn't saved.
export function buildSheetColumnMappings(
  fields: ResolvedWorksheetMapping,
  sheetMappingsForWorksheet: SheetMapping[],
  columnSources: ColumnSource[],
  rowIdentifierFields: string[]
): NewSheetColumnMappings[] {
  const sheetMappings = [...sheetMappingsForWorksheet].sort((a, b) => a.sourceOrder - b.sourceOrder);
  const sheetMappingBySourceOrder = new Map(
    sheetMappings.map((sheetMapping) => [sheetMapping.sourceOrder, sheetMapping])
  );
  const columnMappingsBySheetMappingID = new Map<string, NewColumnMapping[]>(
    sheetMappings.map((sheetMapping) => [sheetMapping.submissionSheetMappingID, []])
  );

  Object.entries(fields).forEach(([field, columnKey], displayOrder) => {
    const column = columnKey ? parseColumnKey(columnKey) : undefined;
    const sheetMapping = column ? sheetMappingBySourceOrder.get(column.sourceOrder) : sheetMappings[0];
    if (!sheetMapping) return;
    columnMappingsBySheetMappingID.get(sheetMapping.submissionSheetMappingID)?.push({
      targetColumnName: field,
      sourceColumnName: column?.sourceColumn ?? columnKey ?? undefined,
      sourceColumnIndex: columnKey ? findIndexOnSheet(columnSources, columnKey) : undefined,
      // Note: header row detection isn't wired into this payload.
      sourceHeaderRow: undefined,
      displayOrder,
      rowIdentifierOrder: getRowIdentifierOrder(field, rowIdentifierFields),
    });
  });

  return sheetMappings.map((sheetMapping) => ({
    submissionSheetMappingID: sheetMapping.submissionSheetMappingID,
    columnMappingsConfirmed: true,
    columnMappings: columnMappingsBySheetMappingID.get(sheetMapping.submissionSheetMappingID) ?? [],
  }));
}

// The names a mapped column can have in another submission's CSV bundle, where the worksheet's sheets are joined.
// A later sheet's column is named after its sheet only when an earlier sheet has the same header.
export function buildBundleColumnNames(
  mapping: ResolvedMapping,
  sheetMappings: SheetMapping[],
  canonicalWorksheets: string[]
): Record<string, Record<string, string[]>> {
  const sheetMappingsByWorksheet = groupSheetMappingsByWorksheet(sheetMappings, (sheetMapping) =>
    canonicalWorksheetName(sheetMapping.worksheet, canonicalWorksheets)
  );
  return Object.fromEntries(
    Object.entries(mapping).map(([worksheet, fields]) => {
      const sheetMappingsForWorksheet = sheetMappingsByWorksheet.get(worksheet) ?? [];
      const mainSourceOrder = Math.min(...sheetMappingsForWorksheet.map((sheetMapping) => sheetMapping.sourceOrder));
      const columnNamesByField = Object.entries(fields).flatMap(([field, columnKey]) => {
        const column = columnKey ? parseColumnKey(columnKey) : undefined;
        if (!column) return [];
        const { sourceOrder, sourceColumn } = column;
        const sheetName = sheetMappingsForWorksheet.find((sheetMapping) => sheetMapping.sourceOrder === sourceOrder)
          ?.sourceSheetName;
        const isLaterSheet = sourceOrder !== mainSourceOrder && sheetName;
        return [
          [
            field,
            isLaterSheet ? [`${sheetName}${SHEET_COLUMN_SEPARATOR}${sourceColumn}`, sourceColumn] : [sourceColumn],
          ],
        ];
      });
      return [worksheet, Object.fromEntries(columnNamesByField)];
    })
  );
}

export function isMappingUnchanged(
  mappingToSave: MappingWithRowIdentifiers,
  savedMapping: ResolvedMapping | null,
  savedRowIdentifierFields: Record<string, string[]>
): boolean {
  return (
    areWorksheetMappingsEqual(mappingToSave.mapping, savedMapping) &&
    areRowIdentifierFieldsEqual(mappingToSave.rowIdentifierFields, savedRowIdentifierFields)
  );
}

function areWorksheetMappingsEqual(a: ResolvedMapping, b: ResolvedMapping | null): boolean {
  if (!b) return false;
  const aWorksheets = Object.keys(a);
  if (aWorksheets.length !== Object.keys(b).length) return false;
  return aWorksheets.every((worksheet) => {
    const aFields = a[worksheet];
    const bFields = b[worksheet];
    if (!bFields) return false;
    const aNames = Object.keys(aFields);
    if (aNames.length !== Object.keys(bFields).length) return false;
    return aNames.every((field) => (aFields[field] ?? null) === (bFields[field] ?? null));
  });
}

function groupSheetMappingsByWorksheet(
  sheetMappings: SheetMapping[],
  resolveSheetMappingWorksheet: (sheetMapping: SheetMapping) => string | null
): Map<string, SheetMapping[]> {
  const sheetMappingsByWorksheet = new Map<string, SheetMapping[]>();
  for (const sheetMapping of sheetMappings) {
    const worksheet = resolveSheetMappingWorksheet(sheetMapping);
    if (worksheet) {
      sheetMappingsByWorksheet.set(worksheet, [...(sheetMappingsByWorksheet.get(worksheet) ?? []), sheetMapping]);
    }
  }
  return sheetMappingsByWorksheet;
}

// Rows saved before the real header was stored name a later sheet's column "Sheet → Header",
// maybe on another sheet mapping
function buildSavedColumnKey(
  sourceColumnName: string,
  sheetMapping: SheetMapping,
  sheetMappingsForWorksheet: SheetMapping[]
): string {
  const prefixedSheetMapping = sheetMappingsForWorksheet.find(
    (candidate) =>
      candidate.sourceSheetName && sourceColumnName.startsWith(`${candidate.sourceSheetName}${SHEET_COLUMN_SEPARATOR}`)
  );
  if (!prefixedSheetMapping?.sourceSheetName) return buildColumnKey(sheetMapping.sourceOrder, sourceColumnName);
  const prefixLength = `${prefixedSheetMapping.sourceSheetName}${SHEET_COLUMN_SEPARATOR}`.length;
  return buildColumnKey(prefixedSheetMapping.sourceOrder, sourceColumnName.slice(prefixLength));
}

// The column's position on its own sheet
function findIndexOnSheet(columnSources: ColumnSource[], columnKey: string): number | undefined {
  const columnSource = findColumnSource(columnSources, columnKey);
  if (!columnSource) return undefined;
  return columnSources.filter((source) => source.sourceOrder === columnSource.sourceOrder).indexOf(columnSource);
}
